/**
 * Mesure d'audience sans traceur.
 *
 * Le navigateur signale un événement, le serveur incrémente un compteur du
 * jour. Rien n'est écrit sur l'appareil du visiteur — c'est ce qui permet de
 * mesurer sans consentement, et donc de voir 100 % du trafic au lieu des
 * 30 à 60 % qui acceptent une bannière.
 *
 * Un seul réglage change avec le consentement, et c'est dit tel quel dans la
 * bannière : avec l'accord du visiteur, on retient sa provenance dans le
 * stockage de session, ce qui évite de recompter une visite à chaque
 * rechargement de page et garde l'origine de sa commande même s'il revient
 * plus tard. Sans accord, on s'en passe.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const CLE_ARRIVEE = 'afrione_arrivee';

// Paramètres de campagne reconnus. Le serveur applique la même liste ; elle
// est répétée ici pour ne pas transmettre le reste de l'adresse, qui peut
// contenir des informations sans rapport avec la mesure.
const PARAMS_SUIVIS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'gad_source', 'fbclid', 'ttclid',
];

/** Relève la provenance depuis l'adresse courante et le site référent. */
function releverArrivee() {
  const params = {};
  try {
    const recherche = new URLSearchParams(window.location.search);
    PARAMS_SUIVIS.forEach((cle) => {
      const valeur = recherche.get(cle);
      if (valeur) params[cle] = valeur.slice(0, 80);
    });
  } catch (e) {
    // Adresse illisible : on se rabat sur le seul référent.
  }
  return { params, referent: (document.referrer || '').slice(0, 300) };
}

// Relevé une seule fois, au chargement : après quoi la navigation interne
// écraserait le référent d'origine par le site lui-même.
let arrivee = releverArrivee();
let arriveeSignalee = false;

/**
 * Mémorise la provenance pour la durée de la session.
 *
 * Appelé uniquement si le visiteur a accepté la mesure approfondie. Deux
 * bénéfices concrets : une visite n'est plus recomptée à chaque
 * rechargement, et une commande passée après un détour garde sa vraie
 * origine au lieu de devenir « direct ».
 */
export function autoriserMemoireArrivee() {
  try {
    const garde = sessionStorage.getItem(CLE_ARRIVEE);
    if (garde) {
      const reprise = JSON.parse(garde);
      // Une provenance déjà retenue fait foi : c'est la première de la visite.
      if (reprise && (Object.keys(reprise.params || {}).length || reprise.referent)) {
        arrivee = reprise;
      }
      arriveeSignalee = true;  // la visite a déjà été comptée
      return;
    }
    sessionStorage.setItem(CLE_ARRIVEE, JSON.stringify(arrivee));
  } catch (e) {
    // Stockage refusé (navigation privée) : on continue sans mémoire.
  }
}

/** Oublie la provenance retenue, quand le consentement est retiré. */
export function oublierMemoireArrivee() {
  try {
    sessionStorage.removeItem(CLE_ARRIVEE);
  } catch (e) {
    // Rien à faire : il n'y avait rien à oublier.
  }
}

/** Ce que la commande transmet pour savoir d'où vient le client. */
export function arriveeDeLaVisite() {
  return arrivee;
}

function envoyer(corps) {
  const url = `${API}/mesure`;
  const charge = JSON.stringify(corps);
  try {
    // sendBeacon survit à la fermeture de l'onglet et ne retarde jamais
    // l'affichage. fetch(keepalive) sert de secours.
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([charge], { type: 'application/json' }));
      if (ok) return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: charge,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // La mesure ne doit jamais gêner un visiteur : on abandonne en silence.
  }
}

/**
 * Signale un événement.
 *
 * La provenance n'accompagne que le premier événement : sans cela, le
 * compteur de provenances suivrait les pages vues et non les visites.
 */
export function signaler(evenement, { chemin = '', produitId = '' } = {}) {
  const corps = { evenement, chemin, produit_id: produitId };
  if (!arriveeSignalee) {
    arriveeSignalee = true;
    corps.arrivee = arrivee;
  }
  envoyer(corps);
}

export const signalerPage = (chemin) => signaler('page', { chemin });
export const signalerFicheVue = (produitId) => signaler('produit_vu', { produitId });
export const signalerAjoutPanier = (produitId) => signaler('panier_ajout', { produitId });
export const signalerTunnel = () => signaler('tunnel');
